import { useEffect, useState } from "preact/hooks"
import { Pencil } from "lucide-react"
import { api_fetch } from "@/api"
import { Input } from "@/components/ui/input"
import type { DeductionKind } from "@/api/deduction.tsx"
import { exactCurrencyFormatter } from "@/utils"
import { display_accounting_amount } from "@/utils/transaction_amount"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import { toast } from "@/components/ui/use-toast.ts"

/** Kinds whose amount a user may correct by hand (the server's `plan_amount_edit` rule). */
export function amount_is_editable(kind: DeductionKind): boolean {
  return kind === "deduction" || kind === "repayment"
}

/**
 * The magnitude a user typed (`200.50`, `$1,234.56`, `-200.5`), in cents; `undefined` when it is not
 * an amount greater than zero. The row keeps its own sign, so a typed minus is ignored.
 */
export function parse_amount_input_cents(value: string): number | undefined {
  const cleaned = value.replace(/[$,\s]/g, "").replace(/^-/, "")
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) {
    return undefined
  }
  const cents = Math.round(parseFloat(cleaned) * 100)
  return cents > 0 ? cents : undefined
}

interface EditableTransactionAmountProps {
  deduction_id: string
  kind: DeductionKind
  invoice_amount: number
  currency?: string
}

/**
 * The large amount at the top of a transaction. For a deduction or repayment it can be corrected
 * to what the backup says (a misread scan); the server refuses once the check is applied.
 */
export function EditableTransactionAmount({ deduction_id, kind, invoice_amount, currency }: EditableTransactionAmountProps) {
  const [editing, set_editing] = useState(false)
  const [value, set_value] = useState("")
  const [saving, set_saving] = useState(false)
  const display = exactCurrencyFormatter(display_accounting_amount(kind, invoice_amount), currency)

  useEffect(() => {
    set_editing(false)
  }, [deduction_id, invoice_amount])

  function start_editing() {
    set_value((Math.abs(Math.round(invoice_amount * 100)) / 100).toFixed(2))
    set_editing(true)
  }

  async function save() {
    const cents = parse_amount_input_cents(value)
    if (cents === undefined) {
      toast({ variant: "destructive", title: "Invalid amount", description: "Enter a dollar amount greater than zero." })
      set_editing(false)
      return
    }
    if (cents === Math.abs(Math.round(invoice_amount * 100))) {
      set_editing(false)
      return
    }
    set_saving(true)
    const response = await api_fetch(`/deduction/${deduction_id}`, { method: "PATCH", body: { invoice_amount: cents / 100 } })
    set_saving(false)
    set_editing(false)
    if (!response.ok) {
      toast({ variant: "destructive", title: "Amount not changed", description: response.error.message })
      return
    }
    toast({ title: "Amount updated", description: "A note on the transaction records the change." })
    triggerRefreshFlag()
  }

  if (!amount_is_editable(kind)) {
    return <span class="text-3xl font-semibold">{display}</span>
  }

  if (editing) {
    return (
      <Input
        autoFocus
        inputMode="decimal"
        autoComplete="off"
        aria-label="Amount"
        className="text-3xl font-semibold h-auto w-56 px-1"
        value={value}
        disabled={saving}
        onInput={(e: Event) => set_value((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Enter") {
            ;(e.currentTarget as HTMLInputElement).blur()
          } else if (e.key === "Escape") {
            set_editing(false)
          }
        }}
        onBlur={save}
      />
    )
  }

  return (
    <button
      type="button"
      title="Correct the amount"
      class="group flex items-center gap-2 text-left w-fit"
      onClick={start_editing}
    >
      <span class="text-3xl font-semibold">{display}</span>
      <Pencil className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100" />
    </button>
  )
}

if (import.meta.vitest) {
  describe("parse_amount_input_cents", () => {
    it("reads what the backup prints", () => {
      expect(parse_amount_input_cents("200.50")).toBe(20050)
      expect(parse_amount_input_cents("$1,234.56")).toBe(123456)
      expect(parse_amount_input_cents("200.5")).toBe(20050)
      expect(parse_amount_input_cents("200")).toBe(20000)
    })
    it("ignores a typed minus: the row keeps its sign", () => {
      expect(parse_amount_input_cents("-200.50")).toBe(20050)
    })
    it("refuses what is not an amount over zero", () => {
      expect(parse_amount_input_cents("")).toBeUndefined()
      expect(parse_amount_input_cents("0")).toBeUndefined()
      expect(parse_amount_input_cents("abc")).toBeUndefined()
      expect(parse_amount_input_cents("1.234")).toBeUndefined()
    })
  })
  describe("amount_is_editable", () => {
    it("allows deductions and repayments only", () => {
      expect(amount_is_editable("deduction")).toBe(true)
      expect(amount_is_editable("repayment")).toBe(true)
      expect(amount_is_editable("invoice")).toBe(false)
      expect(amount_is_editable("prepayment")).toBe(false)
    })
  })
}
